import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { CircleUserIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { Button as AriaButton, FileTrigger } from "react-aria-components";

import { Modal, ModalHeading } from "@/components/base/modal";
import { Uploader } from "@/components/base/upload-image/uploader";
import type { PlayerProfile } from "@/db/schema";
import { useTRPC } from "@/integrations/trpc/react";

type ProfilePhotoProps = Pick<
  PlayerProfile,
  "imageSource" | "preferredName" | "firstName" | "lastName"
> & {
  className?: string;
  /** When provided along with `canUpdate`, the default icon becomes clickable to upload a photo. */
  profileId?: number;
  canUpdate?: boolean;
};

export function ProfilePhoto({
  className = "h-6 w-6",
  imageSource,
  preferredName,
  firstName,
  lastName,
  profileId,
  canUpdate,
}: ProfilePhotoProps) {
  if (imageSource) {
    return (
      <img
        className={clsx(
          "rounded-full overflow-hidden border border-gray-300",
          className,
        )}
        src={imageSource}
        alt={`${preferredName || firstName} ${lastName}`}
      />
    );
  }

  if (canUpdate && profileId) {
    return <EditableDefaultPhoto className={className} profileId={profileId} />;
  }

  return <CircleUserIcon className={className} />;
}

/**
 * Wraps children in a FileTrigger that opens a crop/upload modal for a profile photo.
 * Use this to make any element trigger a profile photo upload flow.
 */
export function ProfilePhotoUpload({
  profileId,
  children,
}: {
  profileId: number;
  children: ReactNode;
}) {
  const [files, setFiles] = useState<File[] | undefined>(undefined);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    ...trpc.profiles.update.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["viewer_profiles"] });
      queryClient.invalidateQueries({
        queryKey: trpc.profiles.get.queryKey({ id: profileId }),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.profiles.getOverview.queryKey({ id: profileId }),
      });
      setFiles(undefined);
    },
  });

  return (
    <>
      <FileTrigger
        acceptedFileTypes={["image/*"]}
        onSelect={(selected) => {
          if (selected) setFiles(Array.from(selected));
        }}
      >
        {children}
      </FileTrigger>

      {files && (
        <Modal
          isOpen={true}
          onOpenChange={(isOpen) => {
            if (!isOpen) setFiles(undefined);
          }}
        >
          <div className="flex flex-col space-y-8 p-3">
            <ModalHeading>Edit your profile photo</ModalHeading>

            <Uploader
              bucket="profiles"
              prefix="profile-photos"
              circular={true}
              initialFiles={files}
              onUploadSuccess={(src) => {
                mutate({ id: profileId, imageSource: src });
              }}
              onCancel={() => setFiles(undefined)}
              onCancelEdit={() => setFiles(undefined)}
            />
          </div>
        </Modal>
      )}
    </>
  );
}

function EditableDefaultPhoto({
  className,
  profileId,
}: {
  className: string;
  profileId: number;
}) {
  return (
    <ProfilePhotoUpload profileId={profileId}>
      <AriaButton
        aria-label="Add profile photo"
        className={clsx(
          "group relative block cursor-pointer rounded-full border-0 bg-transparent p-0",
          className,
        )}
      >
        <CircleUserIcon className="h-full w-full" />
        <span className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 rounded bg-red-600 py-1 text-center text-xs font-bold text-white shadow-md group-hover:bg-red-700">
          ADD PROFILE PHOTO
        </span>
      </AriaButton>
    </ProfilePhotoUpload>
  );
}
